<template>
  <div class="projects-filter-header__wrapper">
    <a-row class="projects-filter-header" :wrap="false">
      <projects-filter-options
        v-if="route.meta.role === USER_ROLE_ADMINISTRATOR"
        :search-params="searchParams"
      />
      <input-search
        :page-name="route.name"
        placeholder="Поиск проектов..."
        :hash="currentView"
      />
      <a-badge
        :count="countFilter"
        :number-style="{
          backgroundColor: '#72c81e',
          color: '#fff',
        }"
      >
        <button-primary @click="openFilter = true"> Фильтр </button-primary>
      </a-badge>
      <ProjectFilterPanel
        :is-open="openFilter"
        :filter-types="filterTypes"
        :filter-showcase-statuses="filterShowcaseStatuses"
        :filter-project-statuses="filterProjectStatuses"
        @update:filter="updateFilter"
        @reset-filter="resetFilter"
        @close="openFilter = false"
      />
    </a-row>
  </div>
</template>

<script setup>
  import { computed, onMounted, ref, watch } from 'vue';
  import { useRoute } from 'vue-router';
  import ProjectFilterPanel from '@/views/projects-showcase/components/ProjectFilterPanel.vue';
  import ButtonPrimary from '@/views/portal/components/UI/ButtonPrimary.vue';
  import {
    PROJECT_TYPE_ID_EDUCATION,
    PROJECT_TYPE_ID_EVENT,
    PROJECT_TYPE_ID_EXPERTISE,
    PROJECT_TYPE_ID_PARTNERSHIP,
    PROJECT_TYPE_ID_PRODUCT,
    PROJECT_TYPE_ID_RESEARCH,
    PROJECT_TYPE_ID_RESOURCES,
    PROJECT_TYPE_ID_STARTUP,
  } from '@/api/models/ProjectType';
  import InputSearch from '@/views/projects-showcase/components/InputSearch.vue';
  import {
    PROJECT_STATUS_NAME_ACCEPTANCE,
    PROJECT_STATUS_NAME_AGREEMENT,
    PROJECT_STATUS_NAME_CLOSED,
    PROJECT_STATUS_NAME_DRAFT,
    PROJECT_STATUS_NAME_IN_WORK,
    PROJECT_STATUS_NAME_REJECTED,
  } from '@/constants/project-status';
  import { PROJECT_TAB_PORTFOLIO } from '@/constants/project-tab';
  import ProjectsFilterOptions from '@/components/projects/components/ProjectsFilterOptions.vue';
  import { USER_ROLE_ADMINISTRATOR } from '@/api/models/UserRole';

  const props = defineProps({
    /**
     * Список названий статусов проектов, которые будут показаны в фильтре статусов проектов.
     */
    statusList: {
      type: Array,
      default: () => [
        PROJECT_STATUS_NAME_ACCEPTANCE,
        PROJECT_STATUS_NAME_IN_WORK,
        PROJECT_STATUS_NAME_CLOSED,
      ],
    },
    /**
     * Список выбранных по умолчанию статусов проектов,
     */
    selectedStatusList: {
      type: Array,
      default: () => [],
    },
  });

  const emit = defineEmits(['update-filter']);
  const route = useRoute();
  const currentView = computed(() => route.hash);
  const openFilter = ref(false);
  const filterProjectStatuses = ref([]);
  const searchParams = ref(null);

  const filterTypes = ref([
    {
      value: PROJECT_TYPE_ID_RESEARCH,
      label: 'Проведение исследовательских работ',
      checked: false,
    },
    { value: PROJECT_TYPE_ID_EDUCATION, label: 'Образование', checked: false },
    {
      value: PROJECT_TYPE_ID_EXPERTISE,
      label: 'Проведение экспертизы',
      checked: false,
    },
    {
      value: PROJECT_TYPE_ID_PARTNERSHIP,
      label: 'Новое сотрудничество',
      checked: false,
    },
    {
      value: PROJECT_TYPE_ID_RESOURCES,
      label: 'Использование ресурсов',
      checked: false,
    },
    { value: PROJECT_TYPE_ID_EVENT, label: 'Мероприятия', checked: false },
    {
      value: PROJECT_TYPE_ID_PRODUCT,
      label: 'Создание продукта',
      checked: false,
    },
    {
      value: PROJECT_TYPE_ID_STARTUP,
      label: 'Стартап',
      checked: false,
    },
  ]);

  const filterShowcaseStatuses = ref([
    {
      value: 'isLookingForPartner',
      label: 'Приглашаем партнеров',
      checked: false,
    },
    {
      value: 'isLookingForStudent',
      label: 'Приглашаем студентов',
      checked: false,
    },
    {
      value: 'isLookingForPerformer',
      label: 'Ищем исполнителей',
      checked: false,
    },
    { value: 'isWaiting', label: 'Выполним похожий проект', checked: false },
  ]);

  const filterProjectStatusList = [
    {
      value: PROJECT_STATUS_NAME_DRAFT,
      label: 'Черновик',
      checked: false,
    },
    {
      value: PROJECT_STATUS_NAME_AGREEMENT,
      label: 'На согласовании',
      checked: false,
    },
    {
      value: PROJECT_STATUS_NAME_IN_WORK,
      label: 'В работе',
      checked: false,
    },
    {
      value: PROJECT_STATUS_NAME_ACCEPTANCE,
      label: 'Приемка',
      checked: false,
    },
    {
      value: PROJECT_STATUS_NAME_CLOSED,
      label: 'Завершен',
      checked: false,
    },
    {
      value: PROJECT_STATUS_NAME_REJECTED,
      label: 'Отклонен',
      checked: false,
    },
  ];

  onMounted(() => {
    filterProjectStatuses.value = getProjectStatuses();
    updateFilter();
  });

  watch(
    () => props.statusList,
    () => {
      filterProjectStatuses.value = getProjectStatuses();
    }
  );

  watch(
    () => route.query.textSearch,
    async () => {
      updateFilter();
    }
  );

  const getProjectStatuses = () => {
    return filterProjectStatusList
      .filter((el) => props.statusList.includes(el.value))
      .map((el) => {
        if (
          props.selectedStatusList &&
          props.selectedStatusList.includes(el.value)
        ) {
          el.checked = true;
        }
        return el;
      });
  };

  const updateFilter = () => {
    searchParams.value = new URLSearchParams({
      role: route.meta.role,
    });

    if (route.query.textSearch) {
      searchParams.value.set('textSearch', route.query.textSearch);
    }

    if (route.meta.type === 'portfolios') {
      searchParams.value.set('briefcaseId', route.params.id);
      searchParams.value.set('tab', PROJECT_TAB_PORTFOLIO);
    }

    filterShowcaseStatuses.value.forEach((el) => {
      if (el.checked) {
        searchParams.value.set(el.value, '1');
      }
    });

    if (filterProjectStatuses.value.some((el) => el.checked)) {
      searchParams.value.set(
        'statuses',
        flattenFilterParams(filterProjectStatuses.value)
      );
    }

    if (filterTypes.value.some((el) => el.checked)) {
      searchParams.value.set('types', flattenFilterParams(filterTypes.value));
    }

    if (route.query.textSearch && route.query.textSearch.length > 0) {
      searchParams.value.set('textSearch', route.query.textSearch);
      searchParams.value.append('sort', '-text_score');
    }

    emit('update-filter', searchParams.value);
    openFilter.value = false;
  };

  const countFilter = computed(() => {
    const typesCount = filterTypes.value.filter((el) => el.checked).length;
    const showcaseStatusesCount = filterShowcaseStatuses.value.filter(
      (el) => el.checked
    ).length;
    const projectStatusesCount = filterProjectStatuses.value.filter(
      (el) => el.checked
    ).length;

    return typesCount + showcaseStatusesCount + projectStatusesCount;
  });

  const resetFilter = async () => {
    filterTypes.value.forEach((el) => (el.checked = false));
    filterShowcaseStatuses.value.forEach((el) => (el.checked = false));
    filterProjectStatuses.value.forEach((el) => (el.checked = false));
    updateFilter();
  };

  /**
   * Преобразует массив фильтров в строку с их значениями, разделёнными запятыми.
   * Пример:
   * Входные данные:
   * [
   *   { value: 'research', label: 'Исследования', checked: true },
   *   { value: 'event', label: 'Мероприятия', checked: true },
   *   'customFilter',
   * ]
   * Результат:
   * 'research,event,customFilter'
   */
  const flattenFilterParams = (filterParams) => {
    return filterParams
      .filter((el) => typeof el === 'string' || el.checked)
      .reduce(
        (acc, el, currentIndex) =>
          acc +
          (currentIndex === 0 ? '' : ',') +
          (typeof el === 'string' ? el : el.value),
        ''
      );
  };
</script>

<style lang="scss" scoped>
  .projects-filter-header__wrapper {
    background: white;
    position: sticky;
    top: -32px;
    z-index: 1;
  }

  .projects-filter-header {
    background: white;
    margin: 0px -40px 0px -20px;
    align-items: center;
    border-bottom: 1px solid #f2f4f8;
    justify-content: space-between;
    gap: 20px;
    padding: 20px 40px 20px 20px;
  }
</style>
